// Inventory Recon — metric definitions + data-source mapping.
//
// Each metric declares how its total is computed. The `source` field is the
// pluggable bit — today only Bulk Dispatch is wired to a real sheet pipeline.
// The rest are marked `tbd` so the page renders the card with a "Source TBD"
// badge and a zero total. When data sources for opening/closing snapshots,
// B2C orders, RTO/RTV/GRN, or F2→F1 transfers become available, swap their
// `source` value and add the corresponding fetcher in InventoryReconPage.
//
// `sign` informs visual outline tone only (the recon formula uses explicit
// signs, not these). `tone` is the outline-only color class for the card.
//   slate   → grey, neutral (opening/closing reference points)
//   amber   → outflow (units leaving inventory)
//   emerald → inflow (units returning to inventory)
//   sky     → derived figure (ideal closing)
//   variance→ stand-out style (red/green based on sign)

// ============ Inventory Recon — SKU inclusion rules ============
// 1) Type rule: DSR keeps multiple rows per SKU when the SKU has variants
//    ("Total / New MRP / Old MRP / New Label / Old Label"). Only the "Total"
//    row is the canonical aggregate; the others are already rolled into Total.
//    Drop rows whose Type is one of those four; keep blank or "Total".
const RECON_DROP_TYPES = new Set(['new mrp', 'old mrp', 'new label', 'old label']);
export const isReconDropType = (typeVal) => RECON_DROP_TYPES.has(
  (typeVal || '').toString().trim().toLowerCase()
);

// 2) Shaker rule: Inventory Recon should consider ONLY the parent shaker
//    rollups ("Steel Shakers" + "Glass Bottle Shaker"). All channel-specific
//    shaker rows (Amazon Steel Shaker, W.in Glass Bottle Shaker, etc.) are
//    excluded so opening/closing/ideal/variance don't double-count.
//    NOTE: this is the INVERSE of Dashboard's `isExcludedFromTotals`, which
//    drops the parents and keeps the channel rows. Different page, different
//    aggregation grain.
const RECON_KEEP_SHAKER_TITLES = new Set(['steel shakers', 'glass bottle shaker']);
export const isReconShakerDrop = (title) => {
  const t = (title || '').toString().trim().toLowerCase();
  if (!t.includes('shaker')) return false;       // not a shaker → keep
  return !RECON_KEEP_SHAKER_TITLES.has(t);       // shaker but not a parent → drop
};

// 3) Synthetic recon SKUs — titles that appear in B2C (and other operational
//    feeds) but are NOT present in `overview_source` / the canonical inventory
//    master. Without these, large slices of B2C consumption land in "unmapped"
//    (e.g., individual wafer bar sales sold as "Pack of 1" while inventory
//    only tracks Pack of 6 / Pack of 10). We surface them as their own
//    recon rows so the operations team sees the consumption + variance.
//    Opening/Closing for these synthetic SKUs default to 0 (no DSR feed).
export const SYNTHETIC_RECON_SKUS = [
  { asin: 'WCCE-WF-P1-OG', brand: 'Wellcore', sku: 'Creatine Orange Wafer Bar (Pack of 1)'    },
  { asin: 'WCCE-WF-P1-CH', brand: 'Wellcore', sku: 'Creatine Chocolate Wafer Bar (Pack of 1)' },
];



export const RECON_METRICS = [
  { key: 'openingStock', label: 'Opening Stock',       source: 'dsr',     tone: 'slate'   },
  { key: 'bulkDispatch', label: 'Bulk Dispatch',       source: 'dispatch', tone: 'amber'   },
  { key: 'b2cOrders',    label: 'B2C Orders',          source: 'b2c',     tone: 'amber'   },
  { key: 'rto',          label: 'RTO',                 source: 'rto',     tone: 'emerald' },
  { key: 'rtv',          label: 'RTV',                 source: 'rtv',     tone: 'emerald' },
  { key: 'grn',          label: 'GRN',                 source: 'grn',     tone: 'emerald' },
  { key: 'f2ToF1',       label: 'F2 → F1',             source: 'f2tof1',  tone: 'amber'   },
  { key: 'idealClosing', label: 'Ideal Closing Stock', source: 'derived', tone: 'sky'     },
  { key: 'closingStock', label: 'Closing Stock',       source: 'dsr',     tone: 'slate'   },
  { key: 'variance',     label: 'Variance',            source: 'derived', tone: 'variance' },
];

// Outline-only Tailwind classes per tone. Keep these `border-*` only — the
// card background stays white per the user's "outline colors only" directive.
export const TONE_OUTLINE = {
  slate:    'border-slate-300',
  amber:    'border-amber-400',
  emerald:  'border-emerald-400',
  sky:      'border-sky-400',
  // Variance is set inline so it can react to sign (red < 0, green ≥ 0).
  variance: 'border-rose-500 ring-2 ring-rose-200',
};

// Recon formula:
//   ideal = opening + rto + rtv + grn - bulk - b2c - f2ToF1
//   variance = closing - ideal
export const computeDerived = (m) => {
  const ideal =
    (m.openingStock || 0)
    + (m.rto || 0) + (m.rtv || 0) + (m.grn || 0)
    - (m.bulkDispatch || 0) - (m.b2cOrders || 0) - (m.f2ToF1 || 0);
  const variance = (m.closingStock || 0) - ideal;
  return { idealClosing: ideal, variance };
};

// ============ SKU-level table support ============
//
// Channel list (user-facing order requested by user).
export const RECON_CHANNELS = [
  'Amazon', 'Flipkart', 'Blinkit', 'W.in',
  'Offline', 'Zepto', 'Meesho', 'FK Mins', 'AZ Now',
  'Tata 1MG',
];

// Map DISPATCH_SOURCES key → user-facing channel label. Meesho has no
// dispatch tab today; it stays at 0 until a source is wired.
export const DISPATCH_TO_CHANNEL = {
  fba: 'Amazon',
  fbf: 'Flipkart',
  blinkit: 'Blinkit',
  win: 'W.in',
  offline: 'Offline',
  zepto: 'Zepto',
  fk_mins: 'FK Mins',
  az_now: 'AZ Now',
  // meesho: 'Meesho',  // ← when a Meesho dispatch tab is added, map it here.
};

// ============ B2C Orders sources ============
// Per-channel daily sales tabs. Layout: Title | SKU 1..4 | <date headers...>
// Each cell value at (row, date-col) = units sold that day for that
// SKU-group. The frontend loops over B2C_SOURCES, walks every cell in
// the date-range window, applies the same multi-SKU full-attribution as
// MTD Sales / RTO, and aggregates by (SKU × channel).
export const B2C_SOURCES = [
  { key: 'amazon',   tab: 'B2C Amazon',   channel: 'Amazon'   },
  { key: 'flipkart', tab: 'B2C FK',       channel: 'Flipkart' },
  { key: 'win',      tab: 'B2C W.in',     channel: 'W.in'     },
  { key: 'meesho',   tab: 'B2C Meesho',   channel: 'Meesho'   },
];


// Maps the raw CHANNEL value in 'Consumer Returns (June 2026 onwards)' to
// the user-facing RECON_CHANNELS labels. Unknown channels fall through
// (stored under their raw label) so ops can spot a new channel immediately.
export const RTO_CHANNEL_MAP = {
  'wellversed.in': 'W.in',
  'meesho':        'Meesho',
  'flipkart':      'Flipkart',
  'amazon':        'Amazon',
};


// One entry per Google Sheets tab fetched by /api/sheets/rtv. The frontend
// loops over RTV_SOURCES, reads the configured date/sku/units columns from
// each tab, applies the selected date range and Mappings-based SKU
// normalisation, then accumulates per-(SKU × channel) into `rtv.byChannel`.
// Adding a new RTV tab requires only a new entry here + a matching key in
// the backend RTV_SHEETS dict — the page totals/cards light up automatically.
//
// `idType` is descriptive only (asin / sku) — the recon engine treats every
// id the same way (looks up `titleByKey` via norm()) so an ASIN-keyed tab
// (BULK Returns Amazon) and a SKU-keyed tab (RTV Returns Amazon) coexist.
export const RTV_SOURCES = [
  { key: 'bulk_amazon',     tab: 'BULK Returns (Amazon)',    dateCol: 'date',        idCol: 'asin',   unitsCol: 'total units received', channel: 'Amazon',    idType: 'asin' },
  // RTV Returns (Amazon) — second Amazon RTV source per user spec
  // (Apr-2026 onwards). Column layout: DATE | SHIPMENT ID | TRACKING ID |
  // ASIN | SKU | TITLE | SELLABLE UNITS | UNSELLABLE UNITS | TOTAL UNITS
  // RECEIVED | DRIVE LINK | REMARKS. We key on ASIN (the SKU column
  // contains Amazon FNSKU codes like 'X001QDP445' that don't exist in the
  // Mappings sheet, while ASIN values like 'B0D9BLY9J9' do). The merge
  // with `bulk_amazon` happens automatically in the page aggregator because
  // both feeds funnel into channel `Amazon`; date-range filter is applied
  // uniformly so historical bulk rows aren't double-counted with newer RTV
  // rows for the same return event.
  { key: 'rtv_amazon',      tab: 'RTV Returns (Amazon)',     dateCol: 'date',        idCol: 'asin',   unitsCol: 'total units received', channel: 'Amazon',    idType: 'asin' },
  { key: 'bulk_flipkart',   tab: 'BULK Returns (Flipkart)',  dateCol: 'date',        idCol: 'sku',    unitsCol: 'total unit',           channel: 'Flipkart',  idType: 'sku'  },
  { key: 'fbw_rtv',         tab: 'FBW RTV',                  dateCol: 'return date', idCol: 'sku-id', unitsCol: 'received units',       channel: 'W.in',      idType: 'sku'  },
  { key: 'amazon_now_rtv',  tab: 'Amazon Now (RTV)',         dateCol: 'date',        idCol: 'sku',    unitsCol: 'total units received', channel: 'AZ Now',    idType: 'sku'  },
  { key: 'rtv_b2b',         tab: 'RTV Returns (B2B)',        dateCol: 'date',        idCol: 'sku-id', unitsCol: 'quantity',             channel: 'Offline',   idType: 'sku'  },
  { key: 'rtv_zepto',       tab: 'RTV Returns (Zepto)',      dateCol: 'date',        idCol: 'sku-id', unitsCol: 'quantity',             channel: 'Zepto',     idType: 'sku'  },
  { key: 'rtv_tata_1mg',    tab: 'RTV Returns (TATA 1MG)',   dateCol: 'date',        idCol: 'sku-id', unitsCol: 'quantity',             channel: 'Tata 1MG',  idType: 'sku'  },
  { key: 'rtv_blinkit',     tab: 'RTV (Blinkit)',            dateCol: 'date',        idCol: 'sku-id', unitsCol: 'quantity',             channel: 'Blinkit',   idType: 'sku'  },
  { key: 'rtv_fk_mins',     tab: 'RTV (FK Mins)',            dateCol: 'date',        idCol: 'sku-id', unitsCol: 'quantity',             channel: 'FK Mins',   idType: 'sku'  },
];

// Metrics that support per-channel expansion in the SKU table. Order matters —
// it controls column order when expanded.
export const EXPANDABLE_METRIC_KEYS = ['bulkDispatch', 'b2cOrders', 'rto', 'rtv'];

// ============================================================================
// SKU-wise Recon Table — section color palette
// ============================================================================
// Each of the 4 expandable metric groups (Bulk Dispatch · B2C Orders · RTO · RTV)
// owns a distinct color "lane" so the section header, the channel sub-headers,
// and (subtly) the body cells stay visually grouped even after the user expands
// channels for one section only. Colors picked to:
//   • read as clearly different at a glance,
//   • all contrast well against the brand Teal (#2EB4AC) on the frozen cols,
//   • all carry white foreground text in header / footer rows.
// One shared bright "Total highlight" sits on top of each section's color so
// the per-section Total sub-col reads instantly across all 4 sections.
export const SECTION_STYLES = {
  bulkDispatch: { header: '#0E7490', sub: '#155E75', body: '#ECFEFF', text: '#FFFFFF' }, // cyan-700 / cyan-800 / cyan-50
  b2cOrders:    { header: '#B45309', sub: '#92400E', body: '#FEF3C7', text: '#FFFFFF' }, // amber-700 / amber-800 / amber-100
  rto:          { header: '#9F1239', sub: '#831843', body: '#FFE4E6', text: '#FFFFFF' }, // rose-800 / rose-900 / rose-100
  rtv:          { header: '#6D28D9', sub: '#5B21B6', body: '#EDE9FE', text: '#FFFFFF' }, // violet-700 / violet-800 / violet-100
};
// Shared bright highlight for the Total column that lives inside each of
// the 4 sections. Golden / amber-300 reads strongly over every section
// header color while keeping the brand-kit Black ink legible.
export const SECTION_TOTAL_HIGHLIGHT = {
  header: '#FACC15',  // amber-400
  body:   '#FEF9C3',  // amber-100
  text:   '#0F0F0F',  // brand ink
};

// Per-metric channel visibility — derived strictly from the configured
// SOURCES so we never render channel columns that have no data path.
//   • Bulk Dispatch → channels reachable via DISPATCH_TO_CHANNEL
//   • RTV           → unique channels listed in RTV_SOURCES
//   • RTO           → keys/values in RTO_CHANNEL_MAP (Amazon/Flipkart/W.in/Meesho)
//   • B2C Orders    → no source yet → returns full list so the UX remains
//                     consistent until a source is wired (no false 0s in
//                     other metrics; B2C cards stay 0 anyway).
// Falls back to RECON_CHANNELS if a metric isn't listed here.
export const channelsForMetric = (metricKey) => {
  if (metricKey === 'bulkDispatch') {
    return RECON_CHANNELS.filter(c => Object.values(DISPATCH_TO_CHANNEL).includes(c));
  }
  if (metricKey === 'rtv') {
    const set = new Set(RTV_SOURCES.map(s => s.channel));
    return RECON_CHANNELS.filter(c => set.has(c));
  }
  if (metricKey === 'rto') {
    const set = new Set(Object.values(RTO_CHANNEL_MAP));
    return RECON_CHANNELS.filter(c => set.has(c));
  }
  if (metricKey === 'b2cOrders') {
    const set = new Set(B2C_SOURCES.map(s => s.channel));
    return RECON_CHANNELS.filter(c => set.has(c));
  }
  return RECON_CHANNELS;
};

// Empty per-channel record (used as default for every SKU's expandable metric).
export const emptyByChannel = () => {
  const o = {};
  RECON_CHANNELS.forEach(ch => { o[ch] = 0; });
  return o;
};

// Build a default per-SKU row shape. Calculation engine populates it.
export const emptyReconSkuRow = (asin = '', brand = '', sku = '') => ({
  asin, brand, sku,
  openingStock: 0,
  // For expandable metrics we store `{ total, byChannel }`. Total is kept in
  // sync with byChannel sums so sorting and the totals row don't have to
  // know about the breakdown.
  bulkDispatch: { total: 0, byChannel: emptyByChannel() },
  b2cOrders:    { total: 0, byChannel: emptyByChannel() },
  rto:          { total: 0, byChannel: emptyByChannel() },
  rtv:          { total: 0, byChannel: emptyByChannel() },
  grn: 0,
  f2ToF1: 0,
  closingStock: 0,
  // derived — populated by computeSkuDerived
  idealClosing: 0,
  variance: 0,
});

// Run the recon formula on a per-SKU row in place. Treats expandable metrics
// as their `.total` for the math.
export const computeSkuDerived = (row) => {
  const totals = {
    openingStock: row.openingStock || 0,
    bulkDispatch: row.bulkDispatch?.total || 0,
    b2cOrders:    row.b2cOrders?.total    || 0,
    rto:          row.rto?.total          || 0,
    rtv:          row.rtv?.total          || 0,
    grn:          row.grn || 0,
    f2ToF1:       row.f2ToF1 || 0,
    closingStock: row.closingStock || 0,
  };
  const d = computeDerived(totals);
  row.idealClosing = d.idealClosing;
  row.variance = d.variance;
  return row;
};

